"use client";

import { useEffect, useRef, ReactNode, useCallback } from "react";
import { useMotionMode } from "@/context/MotionContext";

interface SectionProps {
  children: ReactNode;
  delay?: number;
  className?: string;
  animation?: "fade-up" | "fade-scale" | "fade-blur";
  as?: "div" | "span";
  threshold?: string;
  triggerOnly?: boolean; // Just add .section-visible class, don't animate self
  resetOnLeave?: boolean; // Reset animation when scrolling back up (useful for testing)
}

export function Section({
  children,
  delay = 0,
  className,
  animation = "fade-up",
  as: Component = "div",
  threshold = "top 85%",
  triggerOnly = false,
  resetOnLeave = false,
}: SectionProps) {
  const ref = useRef<HTMLDivElement | HTMLSpanElement>(null);
  const { canAnimate } = useMotionMode();
  const hasAnimatedRef = useRef(false);
  const gsapRef = useRef<typeof import("gsap").gsap | null>(null);
  const animateStateRef = useRef<Parameters<typeof import("gsap").gsap.to>[1] | null>(null);

  // Mouse enter handler to trigger animation immediately
  const handleMouseEnter = useCallback(() => {
    const el = ref.current;
    if (!el || hasAnimatedRef.current || !gsapRef.current || !animateStateRef.current) return;

    hasAnimatedRef.current = true;
    gsapRef.current.to(el, { ...animateStateRef.current, delay: 0 });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Reset animation state when motion mode changes
    hasAnimatedRef.current = false;

    // Skip animations for reduced motion preference or manual motion off
    if (!canAnimate) {
      el.style.opacity = "1";
      el.style.transform = "none";
      el.style.filter = "none";
      hasAnimatedRef.current = true;
      return;
    }

    let ctx: ReturnType<typeof import("gsap").gsap.context> | undefined;
    let isActive = true;

    const initAnimation = async () => {
      const gsap = (await import("gsap")).default;
      const { ScrollTrigger } = await import("gsap/ScrollTrigger");

      // Bail out if effect was cleaned up during async import
      if (!isActive) return;

      gsap.registerPlugin(ScrollTrigger);

      gsapRef.current = gsap;

      // Check if element is already past trigger point (e.g., motion was just enabled while scrolled down)
      const rect = el.getBoundingClientRect();
      const triggerPercent = parseInt(threshold.split(" ")[1]) || 85;
      const triggerPoint = window.innerHeight * (triggerPercent / 100);
      const isAlreadyVisible = rect.top < triggerPoint;

      // triggerOnly mode: just add class when triggered, children handle their own animations
      if (triggerOnly) {
        if (isAlreadyVisible) {
          hasAnimatedRef.current = true;
          el.classList.add("section-visible");
          return;
        }
        ctx = gsap.context(() => {
          ScrollTrigger.create({
            trigger: el,
            start: threshold,
            onEnter: () => {
              if (hasAnimatedRef.current) return;
              hasAnimatedRef.current = true;
              setTimeout(() => el.classList.add("section-visible"), delay);
            },
            onLeaveBack: resetOnLeave ? () => {
              // Reset when scrolling back up past trigger
              hasAnimatedRef.current = false;
              el.classList.remove("section-visible");
            } : undefined,
            once: !resetOnLeave,
          });
        });
        return;
      }

      // If already visible, just show immediately without animation
      if (isAlreadyVisible) {
        el.style.opacity = "1";
        el.style.transform = "none";
        el.style.filter = "none";
        hasAnimatedRef.current = true;
        return;
      }

      // Set initial state based on animation type with will-change hint for GPU optimization
      const initialState: Parameters<typeof gsap.set>[1] = {
        opacity: 0,
        y: animation === "fade-scale" ? 20 : 60,
        willChange: "transform, opacity",
      };

      if (animation === "fade-scale") {
        initialState.scale = 0.95;
      }
      if (animation === "fade-blur") {
        initialState.filter = "blur(10px)";
        initialState.y = 40;
      }

      gsap.set(el, initialState);

      // Create animation state
      const animateState: Parameters<typeof gsap.to>[1] = {
        opacity: 1,
        y: 0,
        duration: 1,
        delay: delay / 1000,
        ease: "power3.out",
        // Clear will-change after animation to free GPU memory
        onComplete: () => {
          if (el) el.style.willChange = "auto";
        },
      };

      if (animation === "fade-scale") {
        animateState.scale = 1;
      }
      if (animation === "fade-blur") {
        animateState.filter = "blur(0px)";
      }

      animateStateRef.current = animateState;

      ctx = gsap.context(() => {
        ScrollTrigger.create({
          trigger: el,
          start: threshold,
          onEnter: () => {
            if (hasAnimatedRef.current) return;
            hasAnimatedRef.current = true;
            gsap.to(el, animateState);
          },
          once: true,
        });
      });

      // Add mouseenter listener for immediate trigger
      el.addEventListener("mouseenter", handleMouseEnter);
    };

    initAnimation();

    return () => {
      isActive = false;
      ctx?.revert();
      el.removeEventListener("mouseenter", handleMouseEnter);
    };
  }, [delay, animation, canAnimate, threshold, triggerOnly, resetOnLeave, handleMouseEnter]);

  return (
    <Component
      ref={ref as React.RefObject<HTMLDivElement & HTMLSpanElement>}
      className={className}
      style={Component === "span" ? { display: "inline" } : undefined}
    >
      {children}
    </Component>
  );
}
